import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a timestamp from the ops API into a real instant.
 *
 * Postgres `timestamp without time zone` columns (voice_interactions.started_at,
 * ended_at, …) come back over the wire as `"2026-08-07 07:45:00.959"` — a space
 * separator, no `T`, no `Z`, no offset. The stored value is UTC, but
 * `new Date("2026-08-07 07:45:00.959")` reads an un-zoned string as LOCAL time,
 * so every timestamp lands off by the viewer's UTC offset. In IST (+05:30) a
 * call that just started reads as "5h ago" — and, worse, exceeding the 2-hour
 * staleness guard makes a live call render as "stale" with no ticking timer.
 *
 * Accepts anything the API might send and normalises it:
 *   "2026-08-07 07:45:00.959"      → treated as UTC
 *   "2026-08-07T07:45:00.959Z"     → already explicit, left alone
 *   "2026-08-07T07:45:00+05:30"    → already explicit, left alone
 *
 * Returns null for null/unparseable input so callers can render "—".
 */
export function parseDbTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const s = String(value).trim();
  // Already carries a zone (trailing Z, or ±HH:MM / ±HHMM) — trust it as-is.
  const zoned = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
  const iso = zoned ? s.replace(" ", "T") : `${s.replace(" ", "T")}Z`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

/** Milliseconds since a DB timestamp, or null when it can't be parsed. */
export function msSince(value: string | null | undefined): number | null {
  const d = parseDbTimestamp(value);
  return d ? Date.now() - d.getTime() : null;
}
